import axios from 'axios';
import t from 'react-translate';

import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  MentorshipProgramConnection,
  MentorshipProgramConnectionMember,
  MentorshipProgramRequest,
} from 'redux/schemas/models/mentorship-program-connections';
import { CreateConnectionType } from 'redux/schemas';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { SingleMentorRequestStatus } from 'redux/schemas/models/mentor-request';
import { addAlertMessage } from './alert-messages';
import { getMentoringProgram } from './mentoring-programs';

type MentorshipProgramParams = {
  mentorshipProgramId: number;
};

type CreateMentorshipProgramConnectionParams = MentorshipProgramParams & {
  connections: { [key: string]: number }[];
};

export const createMentorshipProgramConnection = createAsyncThunk<
MentorshipProgramConnectionMember[],
CreateMentorshipProgramConnectionParams
>(
  'CREATE_MENTORSHIP_PROGRAM_CONNECTION',
  async ({ mentorshipProgramId, connections }) => {
    const response = await axios.post(
      `/mentorship_programs/${mentorshipProgramId}/connections.json`,
      {
        connections,
      },
    );

    return response.data.result;
  },
);

type DeleteMentorshipProgramConnectionParams = MentorshipProgramParams & {
  mentorshipProgramConnectionId: number;
};

export const deleteMentorshipProgramConnection = createAsyncThunk<
boolean,
DeleteMentorshipProgramConnectionParams
>(
  'DELETE_MENTORSHIP_PROGRAM_CONNECTION',
  async ({ mentorshipProgramId, mentorshipProgramConnectionId }) => {
    const response = await axios.delete(
      `/mentorship_programs/${mentorshipProgramId}/connections/${mentorshipProgramConnectionId}.json`,
    );

    return response.data.result;
  },
);

type SessionAvailabilityParams = MentorshipProgramParams & {
  mentorshipProgramConnectionId: number;
  meetingDays: string[];
  meetingDuration: number;
  selectedDate?: string;
};

export type SessionAvailibilitySlot = {
  startDate: string;
  endDate: string;
  loading?: boolean;
};

type SessionAvailabilityRequest = {
  availableDates: SessionAvailibilitySlot[];
};

export const getSessionAvailability = createAsyncThunk<SessionAvailabilityRequest, SessionAvailabilityParams>(
  'GET_SESSION_AVAILABILITY',
  async ({
    mentorshipProgramId,
    mentorshipProgramConnectionId,
    meetingDays,
    meetingDuration,
  }) => {
    const response = await axios.get(
      `/mentorship_programs/${mentorshipProgramId}/connections/${mentorshipProgramConnectionId}/session_availability.json`,
      {
        params: {
          meeting_days: meetingDays,
          meeting_duration: meetingDuration,
        },
      },
    );

    return response.data.result;
  },
);

export type GetMentorshipProgramConnectionParams = MentorshipProgramParams & {
  page: number;
  pageSize: number;
  textSearch?: string;
  // At the moment we can only sort using column 'title', we might add more columns in the future and have more sorting capabilities
  sortBy?: 'title';
  sortDesc?: boolean;
};

const sortByToParamPrefix = {
  title: 'most_recent_',
};

export const getMentorshipProgramConnections = createAsyncThunk<
MentorshipProgramConnection[],
GetMentorshipProgramConnectionParams
>(
  'GET_MENTORSHIP_PROGRAM_CONNECTIONS',
  async ({ mentorshipProgramId, page, pageSize, sortBy, sortDesc, textSearch }) => {
    let sortingParam;
    if (sortBy) {
      const suffix = sortDesc ? 'desc' : 'asc';
      sortingParam = `${sortByToParamPrefix[sortBy]}${suffix}`;
    }

    const params = {
      page,
      page_size: pageSize,
      text_search: textSearch || undefined,
    };

    if (sortingParam) {
      params[sortingParam] = true;
    }

    const response = await axios.get(
      `/mentorship_programs/${mentorshipProgramId}/connections.json`,
      {
        params,
      },
    );

    return response.data.result;
  },
);

export const setCreateConnectionModalSettings = createAction<{
  showModal: boolean;
  type: CreateConnectionType;
  enrollmentId: number;
}>('SET_CREATE_CONNECTION_MODAL_SETTINGS');

export type GetMentorRequestsParams = MentorshipProgramParams & {
  page?: number;
  pageSize?: number;
  enrollmentId?: number;
  state?: SingleMentorRequestStatus;
};

export const getMentorRequests = createAsyncThunk<any, GetMentorRequestsParams>(
  'GET_MENTOR_REQUESTS',
  async ({
    mentorshipProgramId,
    page,
    pageSize,
    enrollmentId,
    state,
  }) => {
    const response = await axios.get(
      `/mentorship_programs/${mentorshipProgramId}/connection_requests`,
      {
        params: {
          page,
          page_size: pageSize,
          mentee_enrollment_id: enrollmentId,
          state: state || undefined,
        },
      },
    );

    return response.data.result;
  },
);

export type GetMentorRequestParams = MentorshipProgramParams & {
  mentorRequestId: number;
};

export const getMentorRequest = createAsyncThunk<any, GetMentorRequestParams>(
  'GET_MENTOR_REQUEST',
  async ({ mentorshipProgramId, mentorRequestId }) => {
    const response = await axios.get(
      `/mentorship_programs/${mentorshipProgramId}/connection_requests/${mentorRequestId}`,
    );

    return response.data.result;
  },
);

export const getPlainTextFromHTML = (htmlString) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  return tempDiv.textContent;
};

export type SendMentorRequestParams = MentorshipProgramParams & {
  mentorEnrollmentId: number;
  message: string;
  fullName: string;
};

export const sendMentorRequest = createAsyncThunk<boolean, SendMentorRequestParams>(
  'SEND_MENTOR_REQUEST',
  async ({
    mentorshipProgramId,
    mentorEnrollmentId,
    message,
    fullName,
  }, { dispatch, rejectWithValue }) => {
    const response = await axios.post(
      `/mentorship_programs/${mentorshipProgramId}/connection_requests`,
      {
        mentor_enrollment_id: mentorEnrollmentId,
        message: getPlainTextFromHTML(message),
      },
    ).then((res) => {
      dispatch(addAlertMessage({
        type: AlertMessageType.SUCCESS,
        header: t.FORM.SUCCESS_BANG(),
        message: t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MENTOR_REQUEST_MODAL.MENTEE_VIEW.SUCCESS_ALERT(fullName),
      }));
      return res.data.result;
    }).catch((error) => {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG_SHORT(),
      }));
      return rejectWithValue(error.response.data.error);
    });

    return response;
  },
);

export type UpdateMentorRequestParams = MentorshipProgramParams & {
  state: SingleMentorRequestStatus;
  mentorRequestId: number;
  menteeEnrollmentId?: number;
};

export const updateMentorRequest = createAsyncThunk<MentorshipProgramRequest, UpdateMentorRequestParams>(
  'UPDATE_MENTOR_REQUEST',
  async ({
    mentorRequestId,
    mentorshipProgramId,
    menteeEnrollmentId,
    state,
  }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.put(
        `/mentorship_programs/${mentorshipProgramId}/connection_requests/${mentorRequestId}`,
        { state },
      );

      if (state === SingleMentorRequestStatus.WITHDRAW) {
        dispatch(
          addAlertMessage({
            type: AlertMessageType.SUCCESS,
            header: t.FORM.SUCCESS_BANG(),
            message:
              t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MENTOR_REQUEST_MODAL.MENTEE_VIEW.WITHDRAW_SUCCESS_ALERT(),
          })
        );
      } else if (state === SingleMentorRequestStatus.APPROVED) {
        await dispatch(getMentoringProgram(mentorshipProgramId));
        setTimeout(() => {
          dispatch(redirectToNewConnectionOnApproval({ enrollmentId: menteeEnrollmentId }));
        }, 500);
      }

      return response.data.result;
    } catch (error: any) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG_SHORT(),
      }));
      return rejectWithValue(error.response.data.error);
    }
  },
);

export const setMentorRequestCarouselIndex = createAction<number>('SET_MENTOR_REQUEST_CAROUSEL_INDEX');

export const resetMentorRequestCarouselIndex = createAction('RESET_MENTOR_REQUEST_CAROUSEL_INDEX');

export const redirectToNewConnectionOnApproval = createAction<{ enrollmentId: number | null }>('REDIRECT_TO_NEW_CONNECTION_ON_APPROVAL');
